/**
 * The walls of a Vorgang that carry no measure, and what the board says about
 * them (UX-Gutachten 23.10.2026, Nr. 6 — „die Rechteck-Falle").
 *
 * A wall without a stated length takes no grating (E-14). The calculation
 * names each such wall as a hint to tick — „hier liegt kein Rost“ — and the
 * release asks for that tick (E-65, D-14 Fassung 2); it used to stop the
 * calculation as a blocker, until every wall was measured or gone. The board
 * used to say only that „Alle Wände belegen“ had left them out — the
 * consequence came on the next screen. So the board says what is still to
 * do, for as long as such a wall stands: tick it as „kein Rost“ before the
 * release. A notice, never a lock: measures that come later („Maße kommen
 * noch“) are an ordinary Vorgang.
 */
import { carriesMeasure } from "@/lib/outline";

/**
 * „Wand 2“ · „Wände 2 und 4“ · „Wände 1, 2 und 4“ — zero-based indices, the
 * numbers the board shows (`index + 1`), as a person reads them out.
 */
export function wallNames(indices: readonly number[]): string {
  const numbers = indices.map((index) => String(index + 1));
  if (numbers.length === 1) return `Wand ${numbers[0]}`;
  return `Wände ${numbers.slice(0, -1).join(", ")} und ${numbers.at(-1)}`;
}

/** Where the board stands on a Vorgang: what the release will ask of these walls. */
export function unmeasuredWallsSentence(indices: readonly number[]): string {
  return `${wallNames(indices)} ohne Maß — vor der Freigabe als „kein Rost“ quittieren.`;
}

/**
 * The register demo's own board has no release to stop — there, as before,
 * only what the laying left out.
 */
export function skippedWallsSentence(indices: readonly number[]): string {
  const names = wallNames(indices);
  return indices.length === 1
    ? `${names} trägt kein Maß und wurde nicht belegt.`
    : `${names} tragen kein Maß und wurden nicht belegt.`;
}

/**
 * Every wall whose length nobody stated — read off the drawing or typed —
 * by index. The calculation counts the same walls the same way
 * (`routes/calculation._walls_from_answer`: `length_source in {read,
 * manual}`), so the board names exactly the walls the release will ask a
 * tick for.
 *
 * Not a wall whose stated figure the sketch contradicts (E-64): it carries no
 * length until a person decides, but „hier liegt kein Rost“ is no answer to a
 * disputed figure — the calculation blocks on it instead
 * (`pricing/bom.walls_without_measure`), and the board asks its own question.
 */
export function unmeasuredWallsOf(
  walls: readonly {
    lengthMm: number | null;
    lengthSource?: string | null;
    implausibleLengthMm?: number | null;
  }[],
): number[] {
  return walls.flatMap((wall, index) =>
    carriesMeasure(wall) || wall.implausibleLengthMm != null ? [] : [index],
  );
}
